import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { DwLoggingService, DwMessage, DwMessageSeverity } from '@devwareapps/devware-cap';
import { debounceTime, switchMap, take } from 'rxjs/operators';
import { estimateShipping } from '../../models/shipping-estimate';
import { CardPaymentComponent } from '../card-payment/card-payment.component';
import { CartItemInput, CartLine, CartRecord, CheckoutConfiguration, CommerceProduct, DeliveryInput, OrderConfirmation } from '../../models/commerce.models';
import { FitnessCommerceService } from '../../services/fitness-commerce.service';
import { PayPalButtons, PayPalSdkService } from '../../services/paypal-sdk.service';
import { DwCountryEntity, DwStateProvinceEntity } from '../../../../meta-data/app-meta-data.service';
import { FitnessReferenceDataService, RegistrationOptions } from '../../../shared/services/fitness-reference-data.service';

@Component({ selector: 'app-fitness-checkout', templateUrl: './fitness-checkout.component.html',
    styleUrls: ['./fitness-checkout.component.scss'] })
export class FitnessCheckoutComponent implements OnInit, OnDestroy {
    @Input() products: CommerceProduct[] = [];
    @Input() configuration: CheckoutConfiguration;
    @Input() registrationOptions: RegistrationOptions = { years: [], sources: [] };
    @Input() draftBusy = false;
    donationPreferencesBusy = false;
    @ViewChild('paypalContainer', { static: false }) paypalContainer: ElementRef<HTMLElement>;
    @ViewChild(CardPaymentComponent) cardPayment: CardPaymentComponent;
    readonly countryLookupId = 263;
    readonly stateLookupId = 278;
    private addressChanges = new Subject<void>();
    previewShipping: number = undefined;
    estimatingShipping = false;
    private promotionPreview: CartRecord;
    private previewRequest = new Subscription();
    private previewRevision = 0;
    stateLookupParams: any[] = [];
    stateOptions: any[] = [];
    private stateCountryId: number;
    private stateOptionsSub: Subscription;
    countryChanged(): void { this.stateLookupParams = [this.delivery.billingAddress.countryId]; this.delivery.billingAddress.stateProvinceId = undefined; this.editChanged(); }
    get summaryCart(): CartRecord {
        if (!this.cart) { return null; }
        if (this.cart.state === 'Cart' && this.promotionPreview) { return this.promotionPreview; }
        const quote = this.cart.cart.quote || { quoteId: '', expiresUtc: '', currency: 'USD',
            subtotal: this.cart.cart.lines.reduce((s,l) => s+l.unitPrice*l.input.quantity,0),
            discount: this.cart.cart.lines.reduce((s,l) => s+(l.discount || 0),0), shipping: null, total: null, issues: [] };
        if (this.cart.state !== 'Cart' || this.previewShipping === undefined) { return { ...this.cart, cart: { ...this.cart.cart, quote } }; }
        return { ...this.cart, cart: { ...this.cart.cart, quote: { ...quote, shipping: this.previewShipping,
            issues: this.previewShipping == null ? quote.issues : quote.issues.filter(i => !['billing_address_required', 'shipping_address_required', 'shipping_destination_unsupported'].includes(i)),
            total: this.previewShipping == null ? null : Math.max(0, Math.round((quote.subtotal + this.previewShipping - quote.discount)*100)/100) } } };
    }
    get payLabel(): string { const q = this.summaryCart?.cart.quote; return !q ? 'Pay' : 'Pay $' + (q.total ?? Math.max(0,q.subtotal-q.discount)).toFixed(2); }
    private updateShippingEstimate(): void {
        this.cancelShippingPreview();
        this.estimatingShipping = false;
        if (!this.cart || this.cart.state !== 'Cart' || !this.configuration?.shippingRates) { return; }
        if (this.cart.cart.promotionCode) {
            // A changed shipping estimate cannot reuse an older promotion discount.
            const address = this.delivery.billingAddress;
            if (!address?.streetAddress1?.trim() || !address.city?.trim() || !address.postalCode?.trim() || !address.countryId) {
                this.previewShipping = null; return;
            }
            const revision = this.previewRevision;
            const delivery: DeliveryInput = { billingAddress: { streetAddress1: address.streetAddress1, city: address.city,
                countryId: address.countryId, stateProvinceId: address.stateProvinceId, postalCode: address.postalCode }, sameAsBilling: true };
            this.estimatingShipping = true; this.previewShipping = null;
            this.previewRequest = this.commerce.previewQuote(delivery).subscribe(preview => {
                if (revision !== this.previewRevision || this.destroyed) { return; }
                if (this.error.startsWith('Unable to update your total.')) { this.error = ''; }
                this.promotionPreview = preview; this.estimatingShipping = false; this.cdr.markForCheck();
            }, () => {
                if (revision !== this.previewRevision || this.destroyed) { return; }
                this.estimatingShipping = false;
                this.error = 'Unable to update your total. Select Pay to check your address and total again.';
                this.cdr.markForCheck();
            });
            return;
        }
        this.previewShipping = estimateShipping(this.cart, this.delivery.billingAddress, this.configuration.shippingRates, this.products);
    }
    private cancelShippingPreview(): void {
        this.previewRevision++; this.previewRequest.unsubscribe(); this.promotionPreview = undefined; this.estimatingShipping = false;
    }
    cart: CartRecord;
    confirmation: OrderConfirmation;
    confirmationLoading = false;
    confirmationError = '';
    private confirmationSub: Subscription;
    get otherParticipants(): OrderConfirmation['participants'] {
        return (this.confirmation?.participants || []).filter(p => !p.isCurrentUser);
    }
    get pendingParticipants(): OrderConfirmation['participants'] { return (this.confirmation?.participants || []).filter(p => !p.registered); }
    get accountSetupEmailPending(): boolean { return (this.confirmation?.participants || []).some(p => p.accountSetupEmailPending); }
    get accountSetupParticipants(): OrderConfirmation['participants'] { return (this.confirmation?.participants || []).filter(p => p.accountSetupRequired || p.accountSetupEmailPending); }
    get purchasedItems(): CartLine[] { return (this.cart?.cart.lines || []).filter(l => l.input.kind === 'product'); }
    get hasShippedItems(): boolean { return this.purchasedItems.some(l => l.requiresShipping && l.input.deliveryMethod === 'mail'); }
    get hasPickupItems(): boolean { return this.purchasedItems.some(l => l.input.deliveryMethod === 'pickup'); }
    delivery: DeliveryInput = { billingAddress: { streetAddress1: '', city: '', countryId: 0, postalCode: '' }, sameAsBilling: true };
    promotion = '';
    outsideUs = false;
    get addressReadOnly(): boolean { return this.busy || this.cardSubmitting || this.draftBusy || this.cart?.state !== 'Cart'; }
    usCountryId = 0;
    private _billingCountry: DwCountryEntity;
    private _billingState: DwStateProvinceEntity;
    private _billingReferenceKey = '';
    private _billingReferenceChanges = new Subject<{ countryId: number; stateId: number }>();
    showCountry(): void { this.outsideUs = true; }
    private defaultCountry(): void {
        if (!this.delivery.billingAddress.countryId && this.usCountryId) { this.delivery.billingAddress.countryId = this.usCountryId; this.deliveryDirty = true; }
        if (this.stateLookupParams[0] !== this.delivery.billingAddress.countryId) { this.stateLookupParams = [this.delivery.billingAddress.countryId]; }
        if (this.delivery.billingAddress.countryId && this.delivery.billingAddress.countryId !== this.usCountryId) { this.outsideUs = true; }
    }
    readonly createCardOrder = (draftId: string) => this.createProviderOrder(draftId, this.cart?.cart.quote?.quoteId);
    readonly approveCardOrder = (data: { orderID: string }, draftId: string) => this.approveProviderOrder(data, draftId);
    readonly payLegacyCard = async (card: { name: string; number: string; expiry: string; cvc: string }, draftId: string): Promise<void> => {
        if (this.destroyed || this.cart?.draftId !== draftId) { throw new Error('Reload checkout.'); }
        const result = await (this.cart.state === 'Pending' ? this.commerce.resumePayment() : this.commerce.checkout('paypal-legacy')).toPromise();
        if (result.status === 'Paid') { return; }
        if (result.provider !== 'paypal-legacy') { throw new Error('Continue with this order’s original payment method.'); }
        const payment = await this.commerce.legacyPayPalCard(card).toPromise();
        this.message = payment.status === 'Paid' ? 'Payment received.' : 'Payment is awaiting confirmation. Refresh status before trying again.';
    };
    cardError(message: string): void { if (this.paymentMethod === 'card') { this.error = message; } }
    processTestPayment(): void {
        if (!this.configuration?.testPayments || this.busy || this.commerce.retryRequired) { return; }
        const request = this.cart?.state === 'Pending' ? this.commerce.resumePayment() : this.commerce.checkout();
        this.run(request.pipe(switchMap(result => this.commerce.restore(result.orderId))), () => {
            this.message = 'Test payment completed. No money was charged.';
        });
    }
    get cardBillingAddress() {
        const a = this.delivery.billingAddress;
        return { addressLine1: a.streetAddress1, addressLine2: a.streetAddress2 || '', adminArea1: this._billingState?.StateProvinceShortName || '',
            adminArea2: a.city, postalCode: a.postalCode, countryCode: this._billingCountry?.CountryShortName || '' };
    }
    busy = false;
    cardSubmitting = false;
    paymentStage: 'checking' | 'settingUp' | 'paying' | 'finishing' = null;
    get paymentButtonBusy(): boolean { return !!(this.busy || this.commerce.busy || this.cardSubmitting || this.draftBusy); }
    get paymentButtonText(): string {
        if (!this.paymentButtonBusy) { return this.payLabel; }
        switch (this.paymentStage) {
            case 'checking': return 'Checking order…';
            case 'settingUp': return 'Setting up payment…';
            case 'paying': return 'Paying…';
            case 'finishing': return 'Finishing order…';
            default: return 'Processing…';
        }
    }
    paymentMethod: 'card' | 'paypal' = 'card';
    usingCardButton = false;
    readonly paymentMethodOptions = { displayStyle: 'blockButtons' as const, stateMode: 'twoState' as const,
        falseLabel: 'Credit or debit card', trueLabel: 'PayPal' };
    selectPaymentMethod(method: 'card' | 'paypal'): void {
        if (this.busy || this.cardSubmitting || this.draftBusy) { return; }
        this.paymentMethod = method; this.error = ''; this.message = ''; this.closeButtons();
    }
    private async submitPayment(): Promise<void> {
        if (this.paymentMethod === 'paypal') { await this.showPayPal(); return; }
        if (!this.cardPayment?.available) {
            await this.showPayPal('card');
            return;
        }
        this.cdr.detectChanges();
        this.paymentStage = 'paying';
        try { await this.cardPayment.pay(); }
        finally { this.paymentStage = null; }
    }
    error = '';
    message = '';
    get pageMessage(): DwMessage {
        const retry = this.commerce.retryRequired && !this.commerce.busy && !this.busy && !this.cardSubmitting && !this.draftBusy
            ? 'Checkout was interrupted. Select Pay to try again.' : '';
        const issues = this.summaryCart?.cart.quote?.issues || [];
        const body = this.error ? this.error
            : retry || (issues.length ? issues.map(issue => this.issueMessage(issue)).join(' ') : '')
            || this.message || (this.cart?.state === 'Paid' ? 'Payment complete.' : '');
        return body ? { messageBody: body, allowClose: false,
            severity: this.error ? DwMessageSeverity.error : retry || issues.length ? DwMessageSeverity.warning
                : this.cart?.state === 'Paid' ? DwMessageSeverity.success : DwMessageSeverity.info } : null;
    }
    accepted = false;
    private deliveryDirty = false;
    private promotionDirty = false;
    get edited(): boolean { return this.deliveryDirty || this.promotionDirty; }
    editingId: string;
    editing: CartItemInput;
    private buttons: PayPalButtons;
    private sub = new Subscription();
    private destroyed = false;
    private paymentRevision = 0;
    private renderedQuote: string;

    constructor(public commerce: FitnessCommerceService, private paypal: PayPalSdkService, private cdr: ChangeDetectorRef,
        private references: FitnessReferenceDataService, private router: Router, private dwLoggingService: DwLoggingService) {}
    ngOnInit(): void {
        this.sub.add(this._billingReferenceChanges.pipe(switchMap(address => combineLatest([
            this.references.getCheckoutCountry(address.countryId),
            this.references.getCheckoutState(address.countryId, address.stateId),
        ]).pipe(take(1)))).subscribe(([country, state]) => {
            this._billingCountry = country; this._billingState = state; this.cdr.markForCheck();
        }, () => this.error = 'Address details could not load. Please reload checkout.'));
        this.sub.add(this.references.getDefaultCheckoutCountry().pipe(take(1)).subscribe(country => {
            this.usCountryId = country?.CountryId || 0;
            if (!this.usCountryId) { this.outsideUs = true; }
            this.defaultCountry(); this.editChanged();
        }, () => this.error = 'Default country could not load. Please reload checkout.'));
        this.sub.add(this.addressChanges.pipe(debounceTime(300)).subscribe(() => this.updateShippingEstimate()));
        this.sub.add(this.commerce.cart$.subscribe(cart => {
            const oldId = this.cart && this.cart.orderId;
            const previousQuote = this.cart && this.cart.cart.quote && this.cart.cart.quote.quoteId;
            this.cart = cart;
            this.cancelShippingPreview();
            this.previewShipping = undefined;
            if (!cart) {
                this.paymentStage = null;
                this.paymentMethod = 'card';
                this.cardSubmitting = false;
                this.confirmationSub?.unsubscribe(); this.confirmation = null; this.confirmationError = ''; this.confirmationLoading = false;
                this.closeButtons(); this.accepted = false; this.deliveryDirty = false; this.promotionDirty = false;
                this.cancelEdit(); this.promotion = ''; this.error = ''; this.message = ''; this.outsideUs = false;
                this.delivery = { billingAddress: { streetAddress1: '', city: '', countryId: 0, postalCode: '' }, sameAsBilling: true };
                return;
            }
            if (oldId !== cart.orderId || !this.edited) {
                if (oldId !== cart.orderId) {
                    this.confirmationSub?.unsubscribe(); this.confirmation = null; this.confirmationError = ''; this.confirmationLoading = false;
                }
                this.delivery = cart.cart.delivery ? JSON.parse(JSON.stringify(cart.cart.delivery)) :
                    { billingAddress: { streetAddress1: '', city: '', countryId: 0, postalCode: '' }, sameAsBilling: true };
                if (!this.delivery.billingAddress) {
                    this.delivery.billingAddress = { streetAddress1: '', city: '', countryId: 0, postalCode: '' };
                }
                if (cart.state === 'Cart' && !this.delivery.billingAddress.streetAddress1?.trim() && !this.delivery.billingAddress.city?.trim() && !this.delivery.billingAddress.postalCode?.trim() && this.configuration?.profileAddress) {
                    this.delivery.billingAddress = { ...this.configuration.profileAddress };
                    this.deliveryDirty = true;
                }
                this.promotion = cart.cart.promotionCode || '';
            }
            if (oldId !== cart.orderId) { this.deliveryDirty = this.deliveryDirty || (!cart.cart.delivery && !!this.configuration?.profileAddress); this.promotionDirty = false; this.accepted = false; this.cancelEdit(); }
            this.defaultCountry();
            this.loadBillingReferences();
            if (previousQuote !== (cart.cart.quote && cart.cart.quote.quoteId) || cart.state !== 'Cart') {
                this.accepted = false;
                if (cart.state !== 'Pending') { this.closeButtons(); }
            }
            this.updateShippingEstimate();
            if (cart.state === 'Paid') {
                this.cardSubmitting = false;
                if (this.confirmation?.orderId !== cart.orderId && !this.confirmationLoading) this.loadConfirmation();
            } else {
                this.confirmationSub?.unsubscribe(); this.confirmation = null; this.confirmationError = ''; this.confirmationLoading = false;
            }
        }));
    }
    ngOnDestroy(): void { this.destroyed = true; this.cancelShippingPreview(); this.sub.unsubscribe(); this.addressChanges.complete(); this._billingReferenceChanges.complete(); this.closeButtons(); }
    get parents(): CartLine[] { return this.cart ? this.cart.cart.lines.filter(l => !l.parentItemId) : []; }
    product(id: number): CommerceProduct { return this.products.find(p => p.productId === +id); }
    issueMessage(issue: string): string {
        const [code, itemId] = issue.split(':');
        const messages = {
            cart_empty: 'Add at least one item to your order.', billing_address_required: 'Enter your shipping address.',
            shipping_address_required: 'Enter your shipping address.',
            shipping_configuration: 'Shipping rates overlap for this destination. Contact us to correct the shipping configuration.',
            duplicate_registration: 'This participant is in the order more than once. Remove the duplicate registration.',
            shipping_destination_unsupported: 'Shipping is not available for this destination. Choose pickup or another address.',
            participant_account_required: 'This participant needs an active account with a verified email before checkout.',
            participant_account_inactive: 'This account needs to be reactivated before registration. Contact us for help.',
            participant_account_ambiguous: 'We need help matching this email to an account. Contact us before continuing.',
            already_registered: 'This participant is already registered for this challenge.',
            registration_reserved: 'Another order already has checkout in progress for this participant.',
            registration_closed: 'Registration for this challenge is closed.', store_closed: 'The challenge store is closed.',
            registration_price: 'Registration pricing is not configured yet. Please contact us.',
            product_kind: 'This product does not match the selected order item. Remove it and select it again.',
            catalog_price: 'This product has an invalid price. Please contact us.',
            quantity: 'Choose a quantity between 1 and 100.',
            delivery_method: 'Choose delivery or pickup for this item.',
            product_unavailable: 'This product is no longer available. Remove it or choose another.',
            promotion_ineligible: 'This promotion does not apply. Change or remove the code.',
            size_required: 'Choose a product size.', color_required: 'Choose a product color.', option_required: 'Choose a product option.',
            variant: 'A selected product option is unavailable. Edit the item.', color: 'A selected color is unavailable. Edit the item.',
            swag_eligibility: 'This included shirt or swag is not eligible. Edit your selection.',
            swag_challenge: 'This shirt or swag is not included in the challenge.',
            shirt_size_mapping: 'This shirt size cannot be registered yet. Choose another size or contact us.',
            active_challenge_configuration: 'The season is not ready for checkout. Please try again later.',
        };
        const line = this.cart && this.cart.cart.lines.find(l => l.itemId === itemId);
        return (line ? (line.input.participant ? line.input.participant.firstName + ' ' + line.input.participant.lastName : line.description) + ': ' : '') +
            (messages[code] || 'An item needs review before checkout. Edit or remove it, then refresh the quote.');
    }
    shippingChanged(): void {
        if (!this.delivery.sameAsBilling && !this.delivery.shippingAddress) {
            this.delivery.shippingAddress = { streetAddress1: '', city: '', countryId: this.usCountryId, postalCode: '' };
        }
        this.editChanged();
    }
    editChanged(): void {
        this.cancelShippingPreview();
        if (this.cart?.state === 'Cart' && this.cart.cart.promotionCode) { this.previewShipping = null; }
        this.loadBillingReferences();
        this.deliveryDirty = true; this.accepted = false; this.closeButtons();
        this.estimatingShipping = true; this.previewShipping = null; this.addressChanges.next();
    }
    private loadBillingReferences(): void {
        const address = this.delivery.billingAddress;
        address.countryId = +address.countryId;
        address.stateProvinceId = address.stateProvinceId ? +address.stateProvinceId : undefined;
        if (this.stateCountryId !== address.countryId) {
            this.stateCountryId = address.countryId;
            this.stateOptions = [];
            this.stateOptionsSub?.unsubscribe();
            this.stateOptionsSub = this.references.getCheckoutStates(address.countryId).pipe(take(1)).subscribe(states => {
                this.stateOptions = states.map(state => ({ Id: String(state.StateProvinceId),
                    Display: state.StateProvinceShortName || state.StateProvinceName }));
                this.cdr.markForCheck();
            }, () => this.error = 'States could not load. Please reload checkout.');
            this.sub.add(this.stateOptionsSub);
        }
        const key = address.countryId + ':' + (address.stateProvinceId || '');
        if (key === this._billingReferenceKey) { return; }
        this._billingReferenceKey = key;
        this._billingCountry = null; this._billingState = null;
        this._billingReferenceChanges.next({ countryId: +address.countryId, stateId: +address.stateProvinceId });
    }
    async payOrder(valid: boolean): Promise<void> {
        if (this.donationPreferencesBusy) { return; }
        if (this.busy || this.commerce.busy || this.cardSubmitting || this.draftBusy || this.estimatingShipping ||
            this.paymentMethod === 'card' && this.cardPayment?.loading) { return; }
        this.error = ''; this.message = '';
        if (this.commerce.retryRequired) {
            this.paymentStage = 'checking';
            this.busy = true; this.error = ''; this.message = ''; this.closeButtons();
            try {
                await this.commerce.retry().pipe(switchMap(result => this.commerce.restore(result.orderId))).toPromise();
                if (this.destroyed || !this.cart || this.cart.state === 'Paid') { return; }
                this.deliveryDirty = false; this.promotionDirty = false;
                if (this.cart.cart.delivery) { this.delivery = JSON.parse(JSON.stringify(this.cart.cart.delivery)); }
                this.promotion = this.cart.cart.promotionCode || '';
                this.cancelEdit();
            } catch (error) { this.report(error); return; }
            finally { this.busy = false; this.paymentStage = null; }
            this.cdr.detectChanges();
        }
        if (!this.cart || this.cart.state === 'Paid') { return; }
        if (this.editing) { this.error = 'Save or cancel the item changes before paying.'; return; }
        if (!valid) { this.error = 'Complete the shipping address before paying.'; return; }
        if (!this._billingCountry || this.delivery.billingAddress.stateProvinceId && !this._billingState) {
            this.error = 'Wait for the selected country and state details to load before paying.'; return;
        }
        if (this.cart.state === 'Pending') {
            if (this.cart.payPalOrderId) {
                this.paymentStage = 'checking';
                this.busy = true;
                try {
                    const result = await this.commerce.capture().toPromise();
                    if (this.destroyed || result.status === 'Paid') { return; }
                } catch (error) { this.report(error); return; }
                finally { this.busy = false; this.paymentStage = null; }
            }
            this.cdr.detectChanges();
            await this.submitPayment();
            return;
        }
        const estimate = this.summaryCart?.cart.quote;
        const displayedTotal = estimate?.total ?? (estimate ? Math.max(0,estimate.subtotal-estimate.discount) : null);
        this.paymentStage = 'checking';
        this.busy = true; this.error = ''; this.message = '';
        try {
            const address = this.delivery.billingAddress;
            const value: DeliveryInput = { billingAddress: { streetAddress1: address.streetAddress1, city: address.city,
                stateProvinceId: address.stateProvinceId, countryId: address.countryId, postalCode: address.postalCode }, sameAsBilling: true };
            const validated = await this.commerce.delivery(value).toPromise();
            if (this.destroyed) { return; }
            this.deliveryDirty = false; this.previewShipping = undefined;
            const quote = validated.cart.quote;
            if (!quote || quote.total == null || quote.issues.length) {
                this.error = quote?.issues.length ? quote.issues.map(issue => this.issueMessage(issue)).join(' ')
                    : 'Shipping could not be calculated for this address. Check the country, state and postal code.';
                return;
            }
            if (displayedTotal != null && Math.abs(displayedTotal-quote.total) > .005) { this.message = 'The total changed after validation. Review the updated amount and select Pay again.'; return; }
            this.accepted = true;
        } catch (error) { this.report(error); return; }
        finally { this.busy = false; this.paymentStage = null; }
        if (this.cart.cart.quote.total === 0) { this.finishFree(); }
        else { await this.submitPayment(); }
    }
    promotionChanged(): void { this.promotionDirty = true; this.accepted = false; this.closeButtons(); }
    get canCheckout(): boolean {
        const q = this.cart && this.cart.cart.quote;
        return !!q && this.cart.state === 'Cart' && this.accepted && !this.edited && !this.editing &&
            !this.busy && !this.draftBusy && !this.donationPreferencesBusy && !this.commerce.retryRequired && q.total != null &&
            Number.isFinite(q.total) && q.issues.length === 0 && Date.parse(q.expiresUtc) > Date.now();
    }
    get canPayCard(): boolean {
        return !this.donationPreferencesBusy && (this.canCheckout || this.cart?.state === 'Pending' && !this.busy && !this.draftBusy && !this.commerce.retryRequired);
    }
    beginEdit(line: CartLine): void {
        this.editingId = line.itemId; this.editing = JSON.parse(JSON.stringify(line.input));
        this.accepted = false; this.closeButtons();
    }
    cancelEdit(): void { this.editing = null; this.editingId = null; }
    saveEdit(valid: boolean): void {
        if (!valid) { this.error = 'Complete the required item fields.'; return; }
        if (this.product(this.editing.productId)?.requiresShipping) { this.editing.deliveryMethod = 'mail'; }
        if (this.editing.swag) { this.editing.swag.deliveryMethod = 'mail'; }
        this.run(this.commerce.saveItem(this.editing, this.editingId), () => this.cancelEdit());
    }
    remove(line: CartLine): void { this.run(this.commerce.remove(line.itemId), () => this.cancelEdit()); }
    saveDelivery(valid: boolean): void {
        if (!valid) { this.error = 'Complete the billing and shipping address fields.'; return; }
        const value: DeliveryInput = JSON.parse(JSON.stringify(this.delivery));
        if (value.sameAsBilling) { delete value.shippingAddress; }
        this.run(this.commerce.delivery(value), () => { this.deliveryDirty = false; this.message = 'Delivery saved. Review the updated total.'; });
    }
    savePromotion(code: string = this.promotion): void {
        this.promotion = code;
        this.run(this.commerce.promotion(this.promotion), () => { this.promotionDirty = false; this.message = 'Promotion checked. Review the updated total.'; });
    }
    summaryCodeEditing(editing: boolean): void {
        this.promotionDirty = editing; this.accepted = false; this.closeButtons();
    }
    refreshQuote(): void {
        this.run(this.commerce.quote(), () => { this.accepted = false; this.message = 'Your total is updated. Review it before paying.'; });
    }
    refreshStatus(): void {
        if (!this.cart) { return; }
        this.run(this.commerce.checkPaymentStatus(), () => {
            this.message = this.cart.state === 'Paid' ? 'Payment received.' : 'Payment has not been confirmed. You can try payment again, change the order, or cancel it. If cancellation is blocked, contact support with your order number.';
        });
    }
    startAnotherOrder(): void { this.run(this.commerce.create(), () => {
        this.router.navigate(['/challenge-registration'], { queryParams: { intent: 'store' } });
        this.message = 'A new order is ready. Return to the store to add items.';
    }); }
    changePendingOrder(keepItems: boolean): void {
        if (this.paymentButtonBusy || this.commerce.retryRequired) { return; }
        this.closeButtons();
        this.busy = true; this.error = ''; this.message = '';
        this.sub.add(this.commerce.cancelOrder(keepItems).subscribe(() => {
            this.busy = false;
            this.previewShipping = undefined;
            this.router.navigate(['/challenge-registration'], { queryParams: { intent: 'store' }, replaceUrl: true });
            this.message = keepItems ? 'Your order is ready to edit.' : 'Order cancelled. You can start a new order.';
        }, error => {
            this.busy = false;
            this.dwLoggingService?.logError(keepItems ? 'Change order failed.' : 'Cancel order failed.', error,
                { orderId: this.cart?.orderId, status: error?.status }, true);
            this.error = error?.error?.detail || error?.error?.message ||
                (keepItems ? 'We could not change this order. Please try again.' : 'We could not cancel this order. Please try again.');
        }));
    }
    retry(): void {
        this.run(this.commerce.retry().pipe(switchMap(result => this.commerce.restore(result.orderId))), () => {
            this.deliveryDirty = false; this.promotionDirty = false;
            if (this.cart.cart.delivery) { this.delivery = JSON.parse(JSON.stringify(this.cart.cart.delivery)); }
            this.promotion = this.cart.cart.promotionCode || '';
            this.cancelEdit(); this.message = 'Your order is ready. Review it before paying.';
        });
    }
    finishFree(): void {
        if (!this.canCheckout || this.cart.cart.quote.total !== 0) { return; }
        this.run(this.commerce.checkout().pipe(switchMap(() => this.commerce.restore(this.cart.orderId))), () => {
            this.message = this.cart.state === 'Paid' ? 'Your order is complete.' : 'Check the order status before continuing.';
        });
    }
    async showPayPal(fundingSource: 'paypal' | 'card' = 'paypal'): Promise<void> {
        const pending = this.cart && this.cart.state === 'Pending';
        if ((!pending && !this.canCheckout) || this.busy || this.cardSubmitting || this.draftBusy || this.commerce.retryRequired) { return; }
        this.error = ''; this.busy = true;
        const draftId = this.cart.draftId, revision = ++this.paymentRevision;
        const quoteId = this.cart.cart.quote.quoteId;
        try {
            const sdk = await this.paypal.load(this.configuration);
            if (this.destroyed || revision !== this.paymentRevision || !this.cart || this.cart.draftId !== draftId) { return; }
            if (this.buttons) { await this.buttons.close(); this.buttons = null; }
            this.renderedQuote = quoteId;
            this.buttons = sdk.Buttons({ fundingSource, style: { layout: 'vertical' },
                createOrder: () => {
                    if (this.cardSubmitting) { throw new Error('Card payment is already being processed.'); }
                    return this.createProviderOrder(draftId, quoteId);
                },
                onApprove: data => this.approveProviderOrder(data, draftId),
                onCancel: () => { this.busy = false; this.message = 'PayPal was closed. Payment has not been confirmed.'; },
                onError: error => { this.busy = false; this.report(error); },
            });
            if (!this.buttons.isEligible()) { throw new Error(fundingSource === 'card'
                ? 'Card payment is unavailable in this browser. Choose PayPal to continue.'
                : 'PayPal is unavailable in this browser.'); }
            this.usingCardButton = fundingSource === 'card';
            this.cdr.detectChanges();
            await this.buttons.render(this.paypalContainer.nativeElement);
            this.message = fundingSource === 'card'
                ? 'Select Debit or Credit Card below to pay securely with PayPal.'
                : 'Choose a payment option below to complete your order.';
        } catch (error) { this.report(error); this.closeButtons(); }
        finally { this.busy = false; }
    }
    private async createProviderOrder(draftId: string, quoteId: string): Promise<string> {
        if (this.destroyed || !this.cart || this.cart.draftId !== draftId) { throw new Error('Reload checkout.'); }
        if (this.cart.state === 'Cart' && (!this.accepted || this.edited || this.editing ||
            this.cart.cart.quote.quoteId !== quoteId || Date.parse(this.cart.cart.quote.expiresUtc) <= Date.now())) {
            throw new Error('Refresh and review the quote before payment.');
        }
        this.paymentStage = 'settingUp'; this.busy = true;
        try {
            const result = await (this.cart.state === 'Pending' ? this.commerce.resumePayment() :
                this.commerce.checkout()).toPromise();
            if (!result.payPalOrderId || result.status === 'Paid') { throw new Error('Refresh this order’s payment status.'); }
            return result.payPalOrderId;
        } finally { this.busy = false; this.paymentStage = this.cardSubmitting ? 'paying' : null; }
    }
    private async approveProviderOrder(data: { orderID: string }, draftId: string): Promise<void> {
        if (!this.cart || this.cart.draftId !== draftId || data.orderID !== this.cart.payPalOrderId) {
            throw new Error('Payment approval did not match this order. Refresh status.');
        }
        this.paymentStage = 'finishing'; this.busy = true;
        try {
            const result = await this.commerce.capture().toPromise();
            this.message = result.status === 'Paid' ? 'Payment received.' :
                'Payment is awaiting confirmation. Refresh status before trying again.';
        } catch (error) { this.report(error); throw error; } finally { this.busy = false; this.paymentStage = null; }
    }
    confirmPayment(): void { this.run(this.commerce.capture(), () => {
        this.message = this.cart.state === 'Paid' ? 'Payment received.' : 'Payment has not yet been confirmed.';
    }); }
    loadConfirmation(retrySetup = false): void {
        if (this.cart?.state !== 'Paid' || this.confirmationLoading) return;
        const orderId = this.cart.orderId;
        this.confirmationLoading = true; this.confirmationError = ''; this.confirmation = null;
        this.confirmationSub?.unsubscribe();
        const request = retrySetup ? this.commerce.retryRegistrationSetup(orderId) : this.commerce.confirmation(orderId);
        this.confirmationSub = request.subscribe(result => {
            if (this.cart?.orderId !== orderId || this.cart.state !== 'Paid') return;
            this.confirmation = result; this.confirmationLoading = false;
        }, () => {
            if (this.cart?.orderId !== orderId || this.cart.state !== 'Paid') return;
            this.confirmationLoading = false;
            this.confirmationError = 'Your payment is complete. We could not load your order details. Please try again.';
        });
        this.sub.add(this.confirmationSub);
    }
    private run(source: any, done: () => void): void {
        if (this.busy || this.cardSubmitting || this.draftBusy) { return; }
        this.busy = true; this.error = ''; this.message = ''; this.accepted = false; this.closeButtons();
        this.sub.add(source.subscribe(() => { this.busy = false; done(); }, error => { this.busy = false; this.report(error); }));
    }
    private report(error: any): void {
        const problem = error && error.error;
        const validation = problem?.errors && Object.keys(problem.errors)
            .map(key => [].concat(problem.errors[key]).join(' ')).join(' ');
        this.error = validation || problem && (problem.detail || problem.message || problem.title)
            || error?.message || 'We could not finish checkout. Please try again.';
        if (/unknown.*error|http failure response/i.test(this.error)) {
            this.error = error?.status === 400 ? 'Review the shipping address and registration selections before paying.'
                : 'We could not confirm your payment. Check your order status before trying again.';
        }
    }
    private closeButtons(): void {
        this.paymentRevision++;
        this.usingCardButton = false;
        if (this.buttons) { this.buttons.close().catch(() => {}); this.buttons = null; }
    }
}







